import React, { useEffect, useState } from 'react';
import { Feedback, useSectionFeedback } from './sectionFeedback';

const DEFAULTS = {
    backgroundColor1: '#263934',
    backgroundColor2: '#1D2E23',
    backgroundColor3: '#B4CCC6',
    textColor1: '#FAFAFA',
    textColor2: '#FFFFFF',
    textColor3: '#1D2E23',
    primaryColor: '#316C29',
};

const COLOR_PRESETS = [
    {
        name: 'Evergreen',
        colors: { ...DEFAULTS },
    },
    {
        name: 'Coastal',
        colors: {
            backgroundColor1: '#153747',
            backgroundColor2: '#20566A',
            backgroundColor3: '#CDE7E8',
            textColor1: '#FFFFFF',
            textColor2: '#FFFFFF',
            textColor3: '#13353F',
            primaryColor: '#0F6572',
        },
    },
    {
        name: 'Midnight',
        colors: {
            backgroundColor1: '#181522',
            backgroundColor2: '#292139',
            backgroundColor3: '#E3D7F4',
            textColor1: '#F8F5FF',
            textColor2: '#FFFFFF',
            textColor3: '#241C31',
            primaryColor: '#6840AE',
        },
    },
    {
        name: 'Sunset',
        colors: {
            backgroundColor1: '#3B1F24',
            backgroundColor2: '#6A3038',
            backgroundColor3: '#F3D7C9',
            textColor1: '#FFF8F2',
            textColor2: '#FFFFFF',
            textColor3: '#352024',
            primaryColor: '#A43D2A',
        },
    },
    {
        name: 'Monochrome',
        colors: {
            backgroundColor1: '#1F2328',
            backgroundColor2: '#343A40',
            backgroundColor3: '#E9ECEF',
            textColor1: '#F6F8FA',
            textColor2: '#FFFFFF',
            textColor3: '#212529',
            primaryColor: '#57606A',
        },
    },
];

function colorValue(value, fallback) {
    return /^#[0-9a-fA-F]{6}$/.test(value || '') ? value : fallback;
}

// Stable reference so `data?.branding || EMPTY_BRANDING` doesn't allocate a fresh
// object literal every render when `data.branding` is absent.
const EMPTY_BRANDING = {};

export default function BrandingSection({ data, saving, onSave, actions }) {
    const branding = data?.branding || EMPTY_BRANDING;
    const previewFont = branding.font || 'Jost';
    const [form, setForm] = useState({});
    const [gradientEnabled, setGradientEnabled] = useState(false);
    const [gradientFrom, setGradientFrom] = useState('#1D2E23');
    const [gradientTo, setGradientTo] = useState('#316C29');
    const [gradientAngle, setGradientAngle] = useState(135);
    const { feedback, showFeedback } = useSectionFeedback();

    useEffect(() => {
        setForm({
            backgroundColor1: colorValue(branding.backgroundColor1, DEFAULTS.backgroundColor1),
            backgroundColor2: colorValue(branding.backgroundColor2, DEFAULTS.backgroundColor2),
            backgroundColor3: colorValue(branding.backgroundColor3, DEFAULTS.backgroundColor3),
            textColor1: colorValue(branding.textColor1, DEFAULTS.textColor1),
            textColor2: colorValue(branding.textColor2, DEFAULTS.textColor2),
            textColor3: colorValue(branding.textColor3, DEFAULTS.textColor3),
            primaryColor: colorValue(branding.primaryColor, DEFAULTS.primaryColor),
        });
        setGradientEnabled(!!branding.backgroundGradient);
        setGradientFrom(colorValue(branding.backgroundGradient?.from, '#1D2E23'));
        setGradientTo(colorValue(branding.backgroundGradient?.to, '#316C29'));
        setGradientAngle(
            typeof branding.backgroundGradient?.angle === 'number'
                ? branding.backgroundGradient.angle
                : 135
        );
        // `branding` falls back to a freshly-allocated `{}` literal when absent from
        // `data`, so depending on it directly would re-run this effect (and re-render)
        // forever. Depend on the primitive fields instead.
    }, [
        branding.backgroundColor1,
        branding.backgroundColor2,
        branding.backgroundColor3,
        branding.textColor1,
        branding.textColor2,
        branding.textColor3,
        branding.primaryColor,
        branding.backgroundGradient?.from,
        branding.backgroundGradient?.to,
        branding.backgroundGradient?.angle,
    ]);

    function updateField(key, value) {
        setForm(prev => ({ ...prev, [key]: value }));
    }

    function applyPreset(colors) {
        setForm(prev => ({ ...prev, ...colors }));
    }

    function isPresetSelected(colors) {
        return Object.entries(colors).every(
            ([key, value]) => form[key]?.toLowerCase() === value.toLowerCase()
        );
    }

    function handleSave() {
        onSave(async () => {
            const backgroundGradient = gradientEnabled
                ? {
                    type: 'linear',
                    angle: parseInt(gradientAngle, 10),
                    from: gradientFrom,
                    to: gradientTo,
                }
                : null;
            const result = await actions.saveBranding({
                ...form,
                backgroundGradient,
            });
            showFeedback(
                result.success ? 'success' : 'error',
                result.success ? 'Colors saved.' : result.error
            );
            return result;
        });
    }

    function handleReset() {
        onSave(async () => {
            const result = await actions.resetBranding();
            showFeedback(
                result.success ? 'success' : 'error',
                result.success ? 'Branding reset.' : result.error
            );
            return result;
        });
    }

    return (
        <div>
            <div className="sfm-section-header">
                <h2>Colors</h2>
                <p>Customize storefront colors and background gradient treatment.</p>
            </div>
            {data?.contrastWarnings?.length ? (
                <div className="sfm-warning">
                    <strong>Color Contrast Warning</strong>
                    <ul>
                        {data.contrastWarnings.map((warning, index) => (
                            <li key={`${warning.pair}-${index}`}>
                                <strong>{warning.pair}</strong>: Contrast ratio is{' '}
                                {warning.ratio}:1.
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
            <fieldset className="sfm-color-presets">
                <legend>Color themes</legend>
                <p className="help">Choose a starting point, then fine-tune any color below.</p>
                <div className="sfm-color-preset-grid">
                    {COLOR_PRESETS.map(preset => {
                        const selected = isPresetSelected(preset.colors);

                        return (
                            <button
                                key={preset.name}
                                type="button"
                                className={`sfm-color-preset${selected ? ' selected' : ''}`}
                                aria-pressed={selected}
                                onClick={() => applyPreset(preset.colors)}
                            >
                                <span
                                    className="sfm-color-preset-swatches"
                                    aria-hidden="true"
                                >
                                    {[1, 2, 3].map(index => (
                                        <span
                                            key={index}
                                            style={{
                                                backgroundColor:
                                                    preset.colors[`backgroundColor${index}`],
                                                color: preset.colors[`textColor${index}`],
                                            }}
                                        >
                                            Aa
                                        </span>
                                    ))}
                                    <span
                                        className="sfm-color-preset-accent"
                                        style={{ backgroundColor: preset.colors.primaryColor }}
                                    />
                                </span>
                                <span>{preset.name}</span>
                            </button>
                        );
                    })}
                </div>
            </fieldset>
            <section
                className="sfm-branding-preview"
                aria-labelledby="sfm-branding-preview-title"
                style={{ fontFamily: `"${previewFont}", Arial, sans-serif` }}
            >
                <div className="sfm-branding-preview-heading">
                    <div>
                        <h3 id="sfm-branding-preview-title">Live storefront preview</h3>
                        <p>Typography and all three color pairs update as you edit.</p>
                    </div>
                    <span>{previewFont}</span>
                </div>
                <div
                    className="sfm-branding-preview-body"
                    style={{
                        background: gradientEnabled
                            ? `linear-gradient(${gradientAngle}deg, ${gradientFrom}, ${gradientTo})`
                            : form.backgroundColor2,
                        color: form.textColor2,
                    }}
                >
                    <aside
                        className="sfm-branding-preview-sidebar"
                        style={{
                            alignSelf: 'stretch',
                            backgroundColor: form.backgroundColor1,
                            color: form.textColor1,
                            height: 'auto',
                            margin: 0,
                        }}
                    >
                        <strong>{data?.basics?.title || 'Your Storefront'}</strong>
                        <span>Featured</span>
                        <span>Shop all</span>
                        <span>About</span>
                    </aside>
                    <div className="sfm-branding-preview-content">
                        <p className="sfm-branding-preview-kicker">New collection</p>
                        <h4>{data?.basics?.subtitle || 'Made for your kind of mayhem'}</h4>
                        <div className="sfm-branding-preview-cards">
                            <article
                                style={{
                                    backgroundColor: form.backgroundColor3,
                                    color: form.textColor3,
                                }}
                            >
                                <span className="sfm-branding-preview-product" />
                                <strong style={{ color: form.primaryColor }}>
                                    Featured product
                                </strong>
                                <span>Product description</span>
                            </article>
                            <article
                                style={{
                                    backgroundColor: form.backgroundColor3,
                                    color: form.textColor3,
                                }}
                            >
                                <span className="sfm-branding-preview-product" />
                                <strong style={{ color: form.primaryColor }}>
                                    Shop favorite
                                </strong>
                                <span>Product description</span>
                            </article>
                        </div>
                        <button
                            type="button"
                            tabIndex={-1}
                            style={{ backgroundColor: form.primaryColor }}
                        >
                            Shop now
                        </button>
                    </div>
                </div>
            </section>
            {[
                [1, 'Primary background and text — main page sections'],
                [2, 'Secondary background and text — alternate sections'],
                [3, 'Accent background and text — cards and highlights'],
            ].map(([index, helpText]) => (
                <div key={index}>
                    <div
                        className="sfm-color-pair"
                        aria-describedby={`sfm-color-pair-help-${index}`}
                    >
                        <div className="group">
                            <label htmlFor={`sfm-bg-${index}`}>Background Color {index}</label>
                            <input
                                id={`sfm-bg-${index}`}
                                type="color"
                                value={form[`backgroundColor${index}`]
                                    || DEFAULTS[`backgroundColor${index}`]}
                                onChange={event => updateField(
                                    `backgroundColor${index}`,
                                    event.target.value
                                )}
                            />
                        </div>
                        <div className="group">
                            <label htmlFor={`sfm-text-${index}`}>Text Color {index}</label>
                            <input
                                id={`sfm-text-${index}`}
                                type="color"
                                value={form[`textColor${index}`]
                                    || DEFAULTS[`textColor${index}`]}
                                onChange={event => updateField(
                                    `textColor${index}`,
                                    event.target.value
                                )}
                            />
                        </div>
                    </div>
                    <p id={`sfm-color-pair-help-${index}`} className="help">{helpText}.</p>
                </div>
            ))}
            <div className="group">
                <label htmlFor="sfm-primary-color">Primary Color</label>
                <input
                    id="sfm-primary-color"
                    type="color"
                    value={form.primaryColor || DEFAULTS.primaryColor}
                    onChange={event => updateField('primaryColor', event.target.value)}
                    aria-describedby="sfm-primary-color-help"
                />
                <p id="sfm-primary-color-help" className="help">
                    Used for buttons, links, and interactive elements.
                </p>
            </div>
            <div className="group">
                <label className="checkbox" htmlFor="sfm-gradient-enabled">
                    <input
                        id="sfm-gradient-enabled"
                        type="checkbox"
                        checked={gradientEnabled}
                        onChange={event => setGradientEnabled(event.target.checked)}
                    />
                    <span>Use Background Gradient</span>
                </label>
            </div>
            {gradientEnabled ? (
                <>
                    <div className="sfm-color-pair">
                        <div className="group">
                            <label htmlFor="sfm-gradient-from">Gradient Start Color</label>
                            <input
                                id="sfm-gradient-from"
                                type="color"
                                value={gradientFrom}
                                onChange={event => setGradientFrom(event.target.value)}
                            />
                        </div>
                        <div className="group">
                            <label htmlFor="sfm-gradient-to">Gradient End Color</label>
                            <input
                                id="sfm-gradient-to"
                                type="color"
                                value={gradientTo}
                                onChange={event => setGradientTo(event.target.value)}
                            />
                        </div>
                    </div>
                    <div className="group">
                        <label htmlFor="sfm-gradient-angle">Gradient Angle</label>
                        <input
                            id="sfm-gradient-angle"
                            type="number"
                            min="0"
                            max="360"
                            value={gradientAngle}
                            onChange={event => setGradientAngle(event.target.value)}
                            aria-describedby="sfm-gradient-angle-help"
                        />
                        <p id="sfm-gradient-angle-help" className="help">
                            Degrees of rotation: 0° = top to bottom, 90° = left to right,
                            135° = diagonal.
                        </p>
                    </div>
                </>
            ) : null}
            <div className="sfm-save-row">
                <button
                    type="button"
                    className="button primary"
                    onClick={handleSave}
                    disabled={saving}
                >
                    Save Colors
                </button>
                <button
                    type="button"
                    className="button secondary"
                    onClick={handleReset}
                    disabled={saving}
                >
                    Reset Branding
                </button>
                <Feedback feedback={feedback} />
            </div>
        </div>
    );
}
